import { useContext, useState, useEffect, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import SEOMetadata from "../SEOMetadata/SEOMetadata";
import useAnalytics from "../../hooks/useAnalytics";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import OpportunityGrid from "../OpportunityGrid/OpportunityGrid";
import ApplicationForm from "../ApplicationForm/ApplicationForm";
import {
  ExclamationCircleIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import {
  FaHeart,
  FaRegHeart,
  FaUserTie,
  FaCalendarAlt,
  FaUniversity,
  FaMapPin,
  FaLinkedin,
  FaTwitter,
  FaFacebook,
  FaInstagram,
  FaEye,
  FaCheckCircle,
} from "react-icons/fa";
import { useMutations } from "../../hooks/useMutations";
import { OpportunityService } from "../../services/OpportunityService";
import { useQuery } from "../../hooks/useQuery";
import { encodeImageUrl, getApiImageUrl } from "../../utils/helpers";
import WetsusBanner from '../WetsusBanner/WetsusBanner';

const IconWithTooltip = ({ icon: Icon, label, color, className, position }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <Icon className={`${color} ${className || "h-5 w-5"}`} />
      </div>
      {showTooltip && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`absolute ${position === "left" ? "right-full" : "left-full"} ml-2 top-1/2 transform -translate-y-1/2 bg-[#1B222A] text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10`}
        >
          {label}
        </motion.div>
      )}
    </div>
  );
};

function OpportunityDetail() {
  const { title } = useParams();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);
  const [savedOpportunities, setSavedOpportunities] = useState(new Set());
  const [relatedOpportunities, setRelatedOpportunities] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [showRelatedSection, setShowRelatedSection] = useState(false);
  const imgRef = useRef(null);
  const location = useLocation();
  const { userDetails: user } = useContext(AuthContext);
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "G-XXXXXXXXXX";
  const { trackEvent } = useAnalytics(GA_MEASUREMENT_ID);

  // Fetch opportunity by ID
  const {
    data: getOpportunityById,
    error: getOpportunityByIdError,
    isLoading: getOpportunityByIdLoading,
    isSuccess: getOpportunityByIdSuccess,
  } = useQuery(
    ["getOpportunityById", title],
    () => OpportunityService.getOpportunityById(title),
    {
      enabled: !!title,
    }
  );

  // Fetch related opportunities
  const fetchRelatedOpportunities = async (currentOpportunity) => {
    if (!currentOpportunity) return;
    
    setLoadingRelated(true);
    try {
      const response = await OpportunityService.getRelatedOpportunities(currentOpportunity.id);
      
      // Handle the backend response format
      if (response && response.data && Array.isArray(response.data)) {
        setRelatedOpportunities(response.data);
        // Show section only if we have more than 1 related opportunity
        setShowRelatedSection(response.data.length >= 1);
      } else {
        // If no data array or empty, set empty array and don't show section
        setRelatedOpportunities([]);
        setShowRelatedSection(false);
      }
    } catch (error) {
      console.error("Error fetching related opportunities:", error);
      // On error, set empty array and don't show section
      setRelatedOpportunities([]);
      setShowRelatedSection(false);
    } finally {
      setLoadingRelated(false);
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [location.pathname]);

  useEffect(() => {
    if (getOpportunityByIdLoading) {
      setLoading(true);
    } else if (getOpportunityByIdSuccess && getOpportunityById) {
      setOpportunity(getOpportunityById);
      const opportunityLink = OpportunityService.generateOpportunityLink(getOpportunityById);
      window.history.pushState({}, "", opportunityLink);
      trackEvent({
        category: "Opportunity",
        action: "View Opportunity Details",
        label: `Opportunity ID: ${title}`,
      });
      setLoading(false);
      
      // Fetch related opportunities after setting the main opportunity
      fetchRelatedOpportunities(getOpportunityById);
    } else if (getOpportunityByIdError) {
      setLoading(false);
    }
  }, [
    getOpportunityByIdLoading,
    getOpportunityByIdSuccess,
    getOpportunityById,
    getOpportunityByIdError,
    title,
  ]);

  useEffect(() => {
    if (opportunity && opportunity.university) {
      const img = new Image();
      img.src = getApiImageUrl('university', `${opportunity.university}.png`);
      img.onload = () => setImgError(false);
      img.onerror = () => setImgError(true);
    }
  }, [opportunity]);

  const {
    data: getSavedOpportunities = [],
    error: savedOppError,
    isLoading: loadingSavedOpp,
    isSuccess: savedOppSuccess,
  } = useQuery(
    ["getSavedOpportunities", user?.id],
    () => OpportunityService.getSavedOpportunities({ userId: user.id }),
    {
      enabled: !!user,
    }
  );

  useEffect(() => {
    const opportunitiesArray = Array.isArray(getSavedOpportunities?.savedOpportunities)
      ? getSavedOpportunities.savedOpportunities
      : [];
    if (savedOppSuccess && opportunitiesArray.length > 0) {
      setSavedOpportunities(new Set(opportunitiesArray));
    }
  }, [getSavedOpportunities, savedOppSuccess]);

  const { mutateAsync: createSaveOpportunity } = useMutations(
    OpportunityService.createSavedOpportunity
  );

  const { mutateAsync: deleteSaveOpportunity } = useMutations(
    OpportunityService.deleteSavedOpportunities
  );

  const toggleSave = (opportunityId) => {
    if (!user) return;
    setSavedOpportunities((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(opportunityId)) {
        newSet.delete(opportunityId);
      } else {
        newSet.add(opportunityId);
      }
      return newSet;
    });
  };

  const handleApplyNowClick = () => {
    trackEvent({
      category: "Opportunity",
      action: "Apply Now Clicked",
      label: `Opportunity ID: ${opportunity.id}`,
    });
  };

  const handleApplicationSuccess = (response) => {
    trackEvent({
      category: "Opportunity",
      action: "Application Submitted",
      label: `Opportunity ID: ${opportunity?.id}`,
    });
  };

  const handleApplicationError = (error) => {
    console.error('Application submission error:', error);
  };

  // Check if resume collection is enabled for this opportunity
  const shouldShowApplicationForm = opportunity?.collect_resumes === true;

  const handleSocialShare = (platform) => {
    trackEvent({
      category: "Social Share",
      action: `Share on ${platform}`,
      label: `Opportunity ID: ${opportunity.id} - ${opportunity.title}`,
    });
  };

  const handleToggleSave = async (opportunityId) => {
    if (!user || !opportunity) return;
    try {
      if (savedOpportunities.has(opportunityId)) {
        await deleteSaveOpportunity({
          user_id: user.id,
          opportunity_id: opportunityId,
        });
        toggleSave(opportunityId);
        trackEvent({
          category: "Opportunity",
          action: "Unsave Opportunity",
          label: `Opportunity ID: ${opportunityId}`,
        });
      } else {
        await createSaveOpportunity({
          user_id: user.id,
          opportunity_id: opportunityId,
        });
        toggleSave(opportunityId);
        trackEvent({
          category: "Opportunity",
          action: "Save Opportunity",
          label: `Opportunity ID: ${opportunityId}`,
        });
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  const removeEmptyTags = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const removeEmpty = (node) => {
      for (let i = node.childNodes.length - 1; i >= 0; i--) {
        const child = node.childNodes[i];
        if (child.nodeType === 1) {
          removeEmpty(child);
          if (
            child.innerHTML.trim() === "" &&
            !["br", "hr"].includes(child.tagName.toLowerCase())
          ) {
            child.parentNode.removeChild(child);
          }
        } else if (child.nodeType === 3 && child.nodeValue.trim() === "") {
          child.parentNode.removeChild(child);
        }
      }
    };
    removeEmpty(doc.body);
    return doc.body.innerHTML;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="animate-spin mx-auto mb-4 h-16 w-16 border-t-4 border-b-4 border-[#DB4F40] rounded-full"></div>
          <p className="text-xl font-semibold text-[#1B222A]">
            Loading opportunity details...
          </p>
        </motion.div>
      </div>
    );
  }

  if (getOpportunityByIdError) {
    return (
      <div className="flex justify-center items-center h-screen">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border-l-4 border-[#DB4F40] text-[#1B222A] p-4 sm:p-6 rounded-lg shadow-lg max-w-sm w-full"
          role="alert"
        >
          <div className="flex items-center">
            <ExclamationCircleIcon className="w-6 h-6 sm:w-8 sm:h-8 mr-3 sm:mr-4 text-[#DB4F40]" />
            <p className="font-bold text-sm sm:text-lg">
              An error occurred while fetching opportunity details.
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-r from-[#FFEEED] to-[#FFD1CE]">
        <div className="text-center">
          <ExclamationCircleIcon className="mx-auto h-16 w-16 text-[#DB4F40]" />
          <p className="mt-4 text-xl font-semibold text-[#1B222A]">
            No details available for this opportunity
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 min-h-screen">
      <WetsusBanner />
      <SEOMetadata
        title={`${opportunity.title} | PhD Position at ${opportunity.university}${opportunity.country ? ` (${opportunity.country})` : ''} | Apply by ${opportunity.deadline || 'Open Deadline'} - PhDScanner`}
        description={`${opportunity.funding ? 'Fully-funded ' : ''}PhD position in ${opportunity.title} at ${opportunity.university}${opportunity.country ? `, ${opportunity.country}` : ''}. ${opportunity.department ? `Department: ${opportunity.department}. ` : ''}${opportunity.deadline ? `Application deadline: ${opportunity.deadline}. ` : 'Rolling admissions. '}Research focus: ${opportunity.researchArea || opportunity.title}. ${opportunity.funding ? `Includes ${opportunity.fundingDetails || 'stipend, tuition waiver, and research budget'}.` : ''}`}
        keywords={[
          `${opportunity.university} PhD positions`,
          `${opportunity.university} jobs`,
          `${opportunity.university} phd jobs`,
          `${opportunity.university} doctoral program`,
          `${opportunity.university} graduate studies`,
          `${opportunity.university} vacancies`,
          `${opportunity.university} research opportunities`,
          `${opportunity.title} PhD`,
          `${opportunity.title} doctoral position`,
          `${opportunity.department} vacancies`,
          `${opportunity.title} Vacancies`,
          opportunity.country ? `PhD in ${opportunity.country}` : '',
          opportunity.country ? `${opportunity.country} PhD` : '',
          opportunity.country ? `${opportunity.country} PhD vacancies` : '',
          opportunity.country ? `${opportunity.country} universities PhD` : '',
          opportunity.country ? `Study in ${opportunity.country}` : '',
          opportunity.country ? `${opportunity.country} fully funded PhD` : '',
          'fully funded PhD',
          'how to apply for PhD',
          'PhD application process',
          'Scholarship Abroad',
          'academic careers',
          'research positions',
          'doctoral studies',
          'PhDScanner opportunities'
        ].filter(Boolean).join(', ')}
        opportunity={opportunity}
        url={window.location.href}
        imageUrl={`https://www.phdscanner.com/university/${encodeURIComponent(opportunity.university.toLowerCase().replace(/\s+/g, '-'))}.webp`}
        effectiveCountry={opportunity.country}
        effectiveUniversity={opportunity.university}
        totalPositions={1}
      />
      
      {/* Breadcrumb Section */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full">
            {opportunity && (
              <Breadcrumb
                items={[
                  { label: "Home", href: "/" },
                  { label: "PhD Openings page", href: "/phd-vacancies/standard" },
                ]}
              />
            )}
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Opportunity Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
        >
          {/* Header Section with Gradient Background */}
          <div className="bg-gradient-to-br from-[#FFF6F3] via-[#FFEEED] to-[#FFD6D4] p-8 lg:p-12 relative overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#DB4F40]/5 rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#5AC1E4]/10 rounded-full translate-y-24 -translate-x-24"></div>
            
            <div className="relative z-10">
              <div className="flex flex-col xl:flex-row gap-8 items-start">
                {/* University Logo Section */}
                <div className="flex-shrink-0 xl:mx-0">
                  <div className="w-32 h-32 xl:w-40 xl:h-40 bg-white rounded-2xl shadow-lg p-6 flex items-center justify-center border-2 border-[#DB4F40]/20">
                    {!imgError ? (
                      <img
                        src={getApiImageUrl('university', `${opportunity.university}.png`)}
                        alt={opportunity.university}
                        className="w-full h-full object-contain transition-transform duration-300 hover:scale-105"
                        onError={() => setImgError(true)}
                      />
                    ) : (
                      <FaUniversity className="h-16 w-16 text-[#DB4F40]/40" />
                    )}
                  </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 text-left">
                  {/* Title and Badges */}
                  <div className="mb-6">
                    <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-[#1B222A] mb-4 leading-tight">
                      {opportunity.title}
                    </h1>
                    
                    {/* Badges */}
                    <div className="flex flex-wrap justify-start gap-3 mb-6">
                      {opportunity.open_to && (
                        <span className="px-4 py-2 bg-[#5AC1E4]/20 text-[#1B222A] rounded-full text-sm font-semibold border border-[#5AC1E4]/30">
                          {opportunity.open_to}
                        </span>
                      )}
                      {opportunity.fully_funded && (
                        <span className="px-4 py-2 bg-[#DB4F40]/20 text-[#1B222A] rounded-full text-sm font-semibold border border-[#DB4F40]/30 flex items-center gap-2">
                          <FaCheckCircle className="text-[#DB4F40]" />
                          Fully Funded
                        </span>
                      )}
                    </div>
                  </div>

                  {/* University and Location */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-8 text-lg">
                    <div className="flex items-center justify-start">
                      <div className="w-10 h-10 bg-[#DB4F40]/10 rounded-full flex items-center justify-center mr-3">
                        <FaUniversity className="text-[#DB4F40] text-lg" />
                      </div>
                      <span className="font-semibold text-[#6C757D]">{opportunity.university}</span>
                    </div>
                    <div className="flex items-center justify-start">
                      <div className="w-10 h-10 bg-[#DB4F40]/10 rounded-full flex items-center justify-center mr-3">
                        <FaMapPin className="text-[#DB4F40] text-lg" />
                      </div>
                      <span className="font-semibold text-[#6C757D]">{opportunity.city}, {opportunity.country}</span>
                    </div>
                  </div>

                  {/* Key Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {opportunity.department && (
                      <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/50 shadow-sm">
                        <div className="flex items-center mb-2">
                          <FaUniversity className="text-[#DB4F40] mr-3 h-5 w-5" />
                          <span className="text-sm font-medium text-[#6C757D] uppercase tracking-wide">Department</span>
                        </div>
                        <span className="text-lg font-semibold text-[#1B222A]">{opportunity.department}</span>
                      </div>
                    )}
                    {opportunity.supervisor && (
                      <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/50 shadow-sm">
                        <div className="flex items-center mb-2">
                          <FaUserTie className="text-[#DB4F40] mr-3 h-5 w-5" />
                          <span className="text-sm font-medium text-[#6C757D] uppercase tracking-wide">Supervisor</span>
                        </div>
                        <span className="text-lg font-semibold text-[#1B222A]">
                          {opportunity.supervisor_url ? (
                            <a href={opportunity.supervisor_url} className="hover:text-[#DB4F40] transition-colors" target="_blank" rel="noopener noreferrer">
                              {opportunity.supervisor}
                            </a>
                          ) : (
                            opportunity.supervisor
                          )}
                        </span>
                      </div>
                    )}
                    {opportunity.closing_date > 0 && (
                      <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/50 shadow-sm">
                        <div className="flex items-center mb-2">
                          <FaCalendarAlt className="text-[#DB4F40] mr-3 h-5 w-5" />
                          <span className="text-sm font-medium text-[#6C757D] uppercase tracking-wide">Deadline</span>
                        </div>
                        <span className={`text-lg font-semibold ${
                          formatDate(opportunity.closing_date) === "Deadline passed" 
                            ? "text-red-600" 
                            : "text-[#1B222A]"
                        }`}>
                          {formatDate(opportunity.closing_date)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mobile Action Buttons */}
                <div className="flex xl:hidden justify-start gap-4 mt-6">
                  {/* Heart Button */}
                  <button
                    onClick={() => user && handleToggleSave(`${opportunity.id}`)}
                    className={`group flex items-center justify-center px-6 py-3 rounded-xl border-2 border-gray-200 hover:border-[#DB4F40] hover:bg-[#DB4F40]/5 transition-all duration-300 ${!user ? "opacity-50 cursor-not-allowed" : ""}`}
                    disabled={!user}
                  >
                    {savedOpportunities.has(`${opportunity.id}`) ? (
                      <FaHeart className="h-5 w-5 text-[#DB4F40] mr-2" />
                    ) : (
                      <FaRegHeart className="h-5 w-5 text-[#DB4F40] mr-2" />
                    )}
                    <span className="text-sm font-medium text-[#1B222A]">
                      {user ? "Save Opportunity" : "Login to Save"}
                    </span>
                  </button>
                  
                  {/* Views Counter */}
                  <div className="bg-white/80 backdrop-blur-sm text-[#DB4F40] px-6 py-3 rounded-xl text-sm flex items-center shadow-sm border border-white/50">
                    <FaEye className="w-4 h-4 mr-2" />
                    <span className="font-semibold">{opportunity.views || opportunity.view_count || 0} views</span>
                  </div>
                </div>

                {/* Right Side Actions - Desktop Only */}
                <div className="hidden xl:flex flex-col items-center gap-4">
                  {/* Heart Button */}
                  <button
                    onClick={() => user && handleToggleSave(`${opportunity.id}`)}
                    className={`group flex flex-col items-center justify-center w-16 h-16 rounded-2xl border-2 border-gray-200 hover:border-[#DB4F40] hover:bg-[#DB4F40]/5 transition-all duration-300 ${!user ? "opacity-50 cursor-not-allowed" : ""}`}
                    disabled={!user}
                  >
                    {savedOpportunities.has(`${opportunity.id}`) ? (
                      <FaHeart className="h-6 w-6 text-[#DB4F40] mb-1" />
                    ) : (
                      <FaRegHeart className="h-6 w-6 text-[#DB4F40] mb-1" />
                    )}
                    <span className="text-xs font-medium text-[#1B222A]/70">
                      {user ? "Save" : "Login"}
                    </span>
                  </button>
                  
                  {/* Views Counter */}
                  <div className="bg-white/80 backdrop-blur-sm text-[#DB4F40] px-4 py-3 rounded-xl text-sm flex items-center shadow-sm border border-white/50">
                    <FaEye className="w-4 h-4 mr-2" />
                    <span className="font-semibold">{opportunity.views || opportunity.view_count || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Section */}
          <div className="p-8 lg:p-12">
            {/* Description */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1B222A] mb-6 flex items-center">
                <div className="w-1 h-8 bg-gradient-to-b from-[#DB4F40] to-[#C64537] rounded-full mr-4"></div>
                Position Description
              </h2>
              <div
                className="prose prose-lg max-w-none text-[#1B222A]/90 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: removeEmptyTags(opportunity.description),
                }}
              />
            </div>

            {/* Action Section */}
            <div className="space-y-8">
              {/* Apply Now Section */}
              {shouldShowApplicationForm ? (
                <div id="application-form" className="bg-gradient-to-br from-[#FFF6F3] to-[#FFEEED] rounded-2xl border-2 border-[#DB4F40]/20 overflow-hidden">
                  <div className="p-8 lg:p-12">
                    <h2 className="text-3xl font-bold text-[#DB4F40] mb-8 flex items-center">
                      <div className="w-1 h-8 bg-gradient-to-b from-[#DB4F40] to-[#C64537] rounded-full mr-4"></div>
                      Apply for this Position
                    </h2>
                    <ApplicationForm
                      opportunityId={opportunity.id}
                      onSuccess={handleApplicationSuccess}
                      onError={handleApplicationError}
                    />
                  </div>
                </div>
              ) : (
                opportunity.opportunity_url && (
                  <div className="text-center">
                    <a
                      href={opportunity.opportunity_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleApplyNowClick}
                      className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#DB4F40] to-[#C64537] text-white font-bold text-lg rounded-2xl hover:from-[#C64537] hover:to-[#B83A2E] hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    >
                      Apply Now
                      <ArrowTopRightOnSquareIcon className="ml-3 h-6 w-6" />
                    </a>
                  </div>
                )
              )}

              {/* Follow Us Section */}
              <div className="bg-gradient-to-br from-[#FFEEED] to-[#FFD6D4] rounded-2xl border-2 border-[#DB4F40]/20 p-8 lg:p-12">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-[#DB4F40] mb-2">
                    Follow us on
                  </h3>
                  <p className="text-lg text-[#1B222A]/80">
                    Join our growing community!
                  </p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {/* LinkedIn */}
                  <a
                    href="https://www.linkedin.com/company/phdscanner"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                    className="group flex flex-col items-center justify-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-[#0077b5]/30"
                    onClick={() => trackEvent({ category: 'Social Follow', action: 'Click', label: 'LinkedIn' })}
                  >
                    <FaLinkedin className="text-[#0077b5] text-4xl mb-3 group-hover:animate-bounce" />
                    <span className="text-[#1B222A] font-bold text-xl mb-1">27k+</span>
                    <span className="text-sm text-[#1B222A]/70 font-medium">Followers</span>
                  </a>
                  
                  {/* Twitter */}
                  <a
                    href="https://x.com/PhdScanner"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Twitter"
                    className="group flex flex-col items-center justify-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-[#1da1f2]/30"
                    onClick={() => trackEvent({ category: 'Social Follow', action: 'Click', label: 'Twitter' })}
                  >
                    <FaTwitter className="text-[#1da1f2] text-4xl mb-3 group-hover:animate-bounce" />
                    <span className="text-[#1B222A] font-bold text-xl mb-1">2k+</span>
                    <span className="text-sm text-[#1B222A]/70 font-medium">Followers</span>
                  </a>
                  
                  {/* Facebook */}
                  <a
                    href="https://www.facebook.com/Phdscanner"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="group flex flex-col items-center justify-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-[#1877f3]/30"
                    onClick={() => trackEvent({ category: 'Social Follow', action: 'Click', label: 'Facebook' })}
                  >
                    <FaFacebook className="text-[#1877f3] text-4xl mb-3 group-hover:animate-bounce" />
                    <span className="text-[#1B222A] font-bold text-xl mb-1">1k+</span>
                    <span className="text-sm text-[#1B222A]/70 font-medium">Followers</span>
                  </a>
                  
                  {/* Instagram */}
                  <a
                    href="https://www.instagram.com/phdscanner"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="group flex flex-col items-center justify-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-[#e4405f]/30"
                    onClick={() => trackEvent({ category: 'Social Follow', action: 'Click', label: 'Instagram' })}
                  >
                    <FaInstagram className="text-[#e4405f] text-4xl mb-3 group-hover:animate-bounce" />
                    <span className="text-[#1B222A] font-bold text-xl mb-1">1k+</span>
                    <span className="text-sm text-[#1B222A]/70 font-medium">Followers</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Related Opportunities Section - Only show if showRelatedSection is true */}
      {showRelatedSection && (
        <section className="py-24 bg-gradient-to-b from-[#FFF6F3] to-[#FFEEED] border-t border-[#F3D1CC]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-extrabold text-[#1B222A] mb-4">
                Related PhD Opportunities
              </h2>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto">
                Discover similar opportunities that might interest you
              </p>
            </div>

            {loadingRelated ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin h-12 w-12 border-t-4 border-b-4 border-[#DB4F40] rounded-full"></div>
              </div>
            ) : (
              // Use single column layout to match the main opportunity grid
              <OpportunityGrid
                opportunities={relatedOpportunities}
                user={user}
                savedOpportunities={savedOpportunities}
                handleToggleSave={handleToggleSave}
                gridCols="grid-cols-1"
                gap="gap-6"
              />
            )}
          </div>
        </section>
      )}
    </div>
  );
}

// Updated DetailCard component
const DetailCard = ({ imgSrc, icon: Icon, title, value, link, className }) => (
  <div className={`bg-white border border-gray-200 rounded-lg p-4 hover:border-[#DB4F40]/20 hover:shadow-md transition-all ${className}`}>
    <div className="flex items-center gap-3 mb-3">
      {imgSrc ? (
        <img src={imgSrc} className="h-8 w-8 text-[#DB4F40]" alt={title} />
      ) : (
        <Icon className="h-8 w-8 text-[#DB4F40] p-1.5 bg-gray-100 rounded-md" />
      )}
      <h3 className="text-base font-semibold text-[#1B222A]">{title}</h3>
    </div>
    {link ? (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#DB4F40] hover:text-[#C64537] transition-colors font-medium text-sm inline-block"
      >
        {value}
      </a>
    ) : (
      <p className="text-[#1B222A]/80 text-sm leading-snug">{value}</p>
    )}
  </div>
);

// Updated Badge component
const Badge = ({ text, color, icon: Icon }) => (
  <div className="flex items-center bg-[#5AC1E4]/20 px-3 py-1.5 rounded-full border border-[#5AC1E4]/30">
    {Icon && <Icon className="h-4 w-4 mr-2 text-[#DB4F40]" />}
    <span className="text-sm font-medium text-[#1B222A]">{text}</span>
  </div>
);

const DetailItem = ({ icon: Icon, label, color, children }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className="flex items-center space-x-3 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300"
  >
    <IconWithTooltip
      icon={Icon}
      label={label}
      color={color}
      className="w-6 h-6 flex-shrink-0"
    />
    <div className="text-gray-700 text-sm sm:text-base">{children}</div>
  </motion.div>
);

const isValidClosingDate = (date) => {
  if (!date) return false;
  const numDate = Number(date);
  return !isNaN(numDate) && numDate > 0 && numDate !== Infinity;
};

const formatDate = (timestamp) => {
  const date = new Date(Number(timestamp) * 1000);
  const now = new Date();
  const diffTime = date - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return "Deadline passed";
  } else {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
};

export default OpportunityDetail;